import React, { useContext, useState, useEffect, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { Meter, MeterType, MeterUnit, Area } from '../types';
import Card from '../components/common/Card';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, MagnifyingGlassIcon, ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';


const EditMeterModal: React.FC<{
  meter: Meter | null;
  areas: Area[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (meter: Meter) => void;
}> = ({ meter, areas, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<Meter | null>(null);

  useEffect(() => {
    setFormData(meter);
  }, [meter]);

  if (!isOpen || !formData) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updatedMeter = { ...formData, [name]: value };
    if (name === 'type') {
      if (value === MeterType.ELECTRICITY) updatedMeter.unit = MeterUnit.KWH;
      else if (value === MeterType.WATER) updatedMeter.unit = MeterUnit.M3;
      else updatedMeter.unit = MeterUnit.TN; // CO2 / VAPOR
    }
    setFormData(updatedMeter);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md relative animate-in fade-in zoom-in duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Editar Medidor</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre del Medidor</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 border" required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Área o Sector</label>
            <select name="area" value={formData.area} onChange={handleChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 border bg-white" required>
              <option value="" disabled>Seleccione un área...</option>
              {areas.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Suministro</label>
            <select name="type" value={formData.type} onChange={handleChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-2.5 border bg-white">
              <option value={MeterType.ELECTRICITY}>Energía Eléctrica (kWh)</option>
              <option value={MeterType.WATER}>Agua (m³)</option>
              <option value={MeterType.CO2}>CO2 (TN)</option>
              <option value={MeterType.VAPOR}>Vapor (TN)</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Guardar Cambios</button>
          </div>
        </form>
      </div>
    </div>
  );
};


const AdminMeters: React.FC = () => {
  const { meters, areas, addMeter, deleteMeter, updateMeter } = useContext(AppContext);
  const [newMeter, setNewMeter] = useState<Omit<Meter, 'id'>>({ name: '', area: '', type: MeterType.ELECTRICITY, unit: MeterUnit.KWH });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingMeter, setEditingMeter] = useState<Meter | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updatedMeter = { ...newMeter, [name]: value };

    if (name === 'type') {
      if (value === MeterType.ELECTRICITY) updatedMeter.unit = MeterUnit.KWH;
      else if (value === MeterType.WATER) updatedMeter.unit = MeterUnit.M3;
      else updatedMeter.unit = MeterUnit.TN; // CO2 / VAPOR
    }

    setNewMeter(updatedMeter);
  };

  const handleAddMeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMeter.name && newMeter.area) {
      addMeter(newMeter);
      setNewMeter({ name: '', area: '', type: MeterType.ELECTRICITY, unit: MeterUnit.KWH });
      setIsCreateModalOpen(false);
    }
  };

  const handleOpenEditModal = (meter: Meter) => {
    setEditingMeter(meter);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingMeter(null);
    setIsEditModalOpen(false);
  };

  const handleSaveChanges = (updatedMeter: Meter) => {
    updateMeter(updatedMeter);
    handleCloseEditModal();
  };

  const filteredMeters = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return meters;
    return meters.filter(m =>
      m.name.toLowerCase().includes(term) ||
      m.area.toLowerCase().includes(term)
    );
  }, [meters, searchTerm]);

  return (
    <div className="w-full space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Registro de Medidores</h1>
          <p className="text-gray-500 mt-1">Gestiona los equipos y puntos de medición de la planta.</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="w-full sm:w-auto flex justify-center items-center py-2.5 px-5 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-brand-primary hover:bg-brand-secondary hover:shadow-lg transform active:scale-[0.98] transition-all"
        >
          <PlusIcon className="h-5 w-5 mr-2 stroke-2" />
          Nuevo Medidor
        </button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-3 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex items-center w-full sm:w-96 bg-gray-50 rounded-xl px-3 border border-transparent focus-within:ring-2 focus-within:ring-brand-primary/20 focus-within:border-brand-primary transition-all">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar medidor por nombre o área..."
            className="w-full bg-transparent border-none focus:ring-0 p-2.5 text-gray-700 placeholder-gray-400 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="hidden sm:flex bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
            title="Vista de Tabla"
          >
            <ListBulletIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('cards')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'}`}
            title="Vista de Tarjetas"
          >
            <Squares2X2Icon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* MOBILE ALL-TIME CARD VIEW */}
      <div className="sm:hidden grid grid-cols-1 gap-4">
        {filteredMeters.length === 0 ? (
          <div className="text-center py-12 px-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-500 font-medium text-lg">No se encontraron medidores</p>
            <p className="text-gray-400 text-sm mt-1">Ajusta tu búsqueda o agrega un nuevo medidor.</p>
          </div>
        ) : (
          filteredMeters.map(meter => (
            <Card key={meter.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group overflow-hidden">
              <div className="flex justify-between items-start mb-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${meter.type === MeterType.ELECTRICITY ? 'bg-amber-100 text-amber-800' :
                  meter.type === MeterType.WATER ? 'bg-blue-100 text-blue-800' :
                    meter.type === MeterType.CO2 ? 'bg-gray-100 text-gray-800' : 'bg-purple-100 text-purple-800'
                  }`}>
                  {meter.type === MeterType.ELECTRICITY ? '⚡ Energía' :
                    meter.type === MeterType.WATER ? '💧 Agua' :
                      meter.type === MeterType.CO2 ? '☁️ CO2' : '💨 Vapor'}
                </span>

                <div className="flex space-x-1 bg-white/90 p-1 rounded-lg border border-gray-100 shadow-sm transition-all duration-200">
                  <button onClick={() => handleOpenEditModal(meter)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteMeter(meter.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="my-4">
                <h3 className="text-xl font-black text-gray-900 leading-tight mb-1">{meter.name}</h3>

              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100/60">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Área</p>
                  <p className="text-sm font-semibold text-gray-800 truncate" title={meter.area}>{meter.area}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Unidad</p>
                  <p className="text-sm font-bold text-gray-900 bg-gray-50 inline-block px-2 py-0.5 rounded-md border border-gray-100">{meter.unit}</p>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* DESKTOP VIEW */}
      <div className="hidden sm:block">
        {viewMode === 'table' ? (
          <Card className="overflow-hidden p-0 shadow-sm border border-gray-100">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Identificador del Equipo</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Área o Sector</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Suministro y Unidad</th>
                    <th scope="col" className="px-3 py-3 sm:px-6 sm:py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredMeters.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <p className="text-gray-500 font-medium text-lg mb-1">No se encontraron medidores</p>
                        <p className="text-gray-400 text-sm">Prueba ajustando los filtros de búsqueda.</p>
                      </td>
                    </tr>
                  ) : (
                    filteredMeters.map(meter => (
                      <tr key={meter.id} className="hover:bg-amber-50/50 transition-colors">
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{meter.name}</div>

                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-800">{meter.area}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${meter.type === MeterType.ELECTRICITY ? 'bg-amber-100 text-amber-800' :
                            meter.type === MeterType.WATER ? 'bg-blue-100 text-blue-800' :
                              meter.type === MeterType.CO2 ? 'bg-gray-100 text-gray-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                            {meter.type === MeterType.ELECTRICITY ? '⚡ Energía' :
                              meter.type === MeterType.WATER ? '💧 Agua' :
                                meter.type === MeterType.CO2 ? '☁️ CO2' : '💨 Vapor'}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md font-bold">{meter.unit}</span>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => handleOpenEditModal(meter)} className="p-2 text-gray-400 hover:text-brand-primary hover:bg-amber-50 rounded-lg transition-colors mr-1" title="Editar">
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button onClick={() => deleteMeter(meter.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredMeters.length === 0 ? (
              <div className="col-span-full text-center py-12 px-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <p className="text-gray-500 font-medium text-lg">No se encontraron medidores</p>
                <p className="text-gray-400 text-sm mt-1">Ajusta tu búsqueda o agrega un nuevo medidor.</p>
              </div>
            ) : (
              filteredMeters.map(meter => (
                <Card key={meter.id} className="hover:shadow-lg transition-all border border-gray-100 hover:border-brand-primary/20 relative group overflow-hidden">
                  <div className="flex justify-between items-start mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${meter.type === MeterType.ELECTRICITY ? 'bg-amber-100 text-amber-800' :
                      meter.type === MeterType.WATER ? 'bg-blue-100 text-blue-800' :
                        meter.type === MeterType.CO2 ? 'bg-gray-100 text-gray-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                      {meter.type === MeterType.ELECTRICITY ? '⚡ Energía' :
                        meter.type === MeterType.WATER ? '💧 Agua' :
                          meter.type === MeterType.CO2 ? '☁️ CO2' : '💨 Vapor'}
                    </span>

                    <div className="flex opacity-100 sm:opacity-0 sm:group-hover:opacity-100 absolute top-4 right-4 space-x-1 bg-white/90 backdrop-blur-sm p-1 rounded-lg border border-gray-100 shadow-sm transition-all duration-200">
                      <button onClick={() => handleOpenEditModal(meter)} className="p-1.5 text-gray-500 hover:text-brand-primary hover:bg-amber-50 rounded-md transition-colors" title="Editar">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteMeter(meter.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Eliminar">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="my-4">
                    <h3 className="text-xl font-black text-gray-900 leading-tight mb-1">{meter.name}</h3>

                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100/60">
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Área Asignada</p>
                      <p className="text-sm font-semibold text-gray-800 truncate" title={meter.area}>{meter.area}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Unidad de Medida</p>
                      <p className="text-sm font-bold text-gray-900 bg-gray-50 inline-block px-2 py-0.5 rounded-md border border-gray-100">{meter.unit}</p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal Creador de Medidores */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative animate-in fade-in zoom-in duration-200">
            <button onClick={() => setIsCreateModalOpen(false)} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors">
              <XMarkIcon className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
              <PlusIcon className="h-7 w-7 mr-2 text-brand-primary" />
              Nuevo Medidor
            </h2>
            <form onSubmit={handleAddMeter} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre</label>
                <input type="text" name="name" value={newMeter.name} onChange={handleInputChange} placeholder="Ej: Compresor Principal 01" className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border placeholder-gray-400 transition-colors" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Área</label>
                <select name="area" value={newMeter.area} onChange={handleInputChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-white transition-colors" required>
                  <option value="" disabled>Seleccione un área...</option>
                  {areas.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
                {areas.length === 0 && <p className="text-xs text-brand-primary mt-1 font-semibold">Primero debe registrar Áreas en la sección Infraestructura.</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Suministro</label>
                <select name="type" value={newMeter.type} onChange={handleInputChange} className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-brand-primary focus:border-brand-primary p-3 border bg-white transition-colors">
                  <option value={MeterType.ELECTRICITY}>Energía Eléctrica (kWh)</option>
                  <option value={MeterType.WATER}>Agua (m³)</option>
                  <option value={MeterType.CO2}>CO2 (TN)</option>
                  <option value={MeterType.VAPOR}>Vapor (TN)</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
                <button type="button" onClick={() => setIsCreateModalOpen(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-colors">Cancelar</button>
                <button type="submit" className="px-5 py-2.5 bg-brand-primary text-white rounded-xl hover:bg-brand-secondary font-medium shadow-md transition-all">Registrar Equipo</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editor de Medidores */}
      <EditMeterModal
        isOpen={isEditModalOpen}
        meter={editingMeter}
        areas={areas}
        onClose={handleCloseEditModal}
        onSave={handleSaveChanges}
      />
    </div>
  );
};

export default AdminMeters;

